import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listEngagements } from './grcClient.js';
import { getFrameworkLabel } from './grcFrameworks.js';
import Workbench from '../components/shared/Workbench.jsx';
import GrcNewClientModal from './GrcNewClientModal.jsx';

const STATUS_COLORS = {
  planning: { bg: 'rgba(148,163,184,0.12)', fg: '#94a3b8', label: 'Planning' },
  active: { bg: 'rgba(99,102,241,0.15)', fg: '#818cf8', label: 'Active' },
  review: { bg: 'rgba(251,191,36,0.10)', fg: '#fbbf24', label: 'Review' },
  'sign-off': { bg: 'rgba(251,191,36,0.15)', fg: '#fbbf24', label: 'Sign-off' },
  concluded: { bg: 'rgba(74,222,128,0.15)', fg: '#4ade80', label: 'Concluded' },
  archived: { bg: 'rgba(148,163,184,0.08)', fg: '#64748b', label: 'Archived' },
  terminated: { bg: 'rgba(251,113,133,0.15)', fg: '#fb7185', label: 'Terminated' },
};

const STATUS_ORDER = [
  'active',
  'review',
  'sign-off',
  'planning',
  'concluded',
  'archived',
  'terminated',
];

function StatusChip({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.planning;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 7px',
        borderRadius: 4,
        background: c.bg,
        color: c.fg,
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
      }}
    >
      {c.label}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function GrcClientList({ session }) {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  function load() {
    setLoading(true);
    listEngagements()
      .then((r) => setEngagements(r.engagements || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
  }, []);

  // Group engagements by clientName → client rows for Workbench
  const rows = useMemo(() => {
    const map = new Map();
    for (const eng of engagements) {
      const name = eng.clientName || 'Unknown';
      if (!map.has(name)) map.set(name, { name, engs: [] });
      map.get(name).engs.push(eng);
    }

    return Array.from(map.values())
      .map(({ name, engs }) => {
        // Sort newest first
        engs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const latest = engs[0];

        // Pick the "dominant" status: highest priority from STATUS_ORDER
        const statusSet = new Set(engs.map((e) => e.status));
        const dominantStatus = STATUS_ORDER.find((s) => statusSet.has(s)) || 'planning';

        // Unique frameworks
        const frameworks = [...new Set(engs.map((e) => e.frameworkId).filter(Boolean))]
          .map(getFrameworkLabel)
          .join(', ');

        return {
          id: name, // Workbench row key
          name,
          cvr: latest?.cvrNumber || null,
          engagementCount: engs.length,
          status: dominantStatus,
          framework: frameworks || null,
          lastActivity: latest?.createdAt || null,
          tryggrLinked: engs.some((e) => !!e.tryggrSlug),
        };
      })
      .sort((a, b) => {
        const ai = STATUS_ORDER.indexOf(a.status);
        const bi = STATUS_ORDER.indexOf(b.status);
        if (ai !== bi) return ai - bi;
        return a.name.localeCompare(b.name);
      });
  }, [engagements]);

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Client',
        accessorKey: 'name',
        sortable: true,
        filter: { kind: 'text' },
        size: 260,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontWeight: 600, color: 'var(--fg)' }}>{row.original.name}</span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        accessorKey: 'status',
        sortable: true,
        groupable: true,
        filter: {
          kind: 'select',
          options: STATUS_ORDER.map((s) => ({
            value: s,
            label: STATUS_COLORS[s]?.label || s,
          })),
        },
        size: 120,
        defaultVisible: true,
        cell: ({ row }) => <StatusChip status={row.original.status} />,
      },
      {
        id: 'framework',
        label: 'Framework',
        accessorKey: 'framework',
        sortable: true,
        groupable: true,
        filter: { kind: 'text' },
        size: 200,
        defaultVisible: true,
        cell: ({ row }) => (
          <span
            style={{
              fontSize: 12,
              color: row.original.framework ? 'var(--accent)' : 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {row.original.framework || '—'}
          </span>
        ),
      },
      {
        id: 'cvr',
        label: 'CVR',
        accessorKey: 'cvr',
        sortable: false,
        filter: { kind: 'text' },
        size: 120,
        defaultVisible: true,
        cell: ({ row }) => (
          <span
            style={{
              fontSize: 12,
              color: row.original.cvr ? 'var(--fg2)' : 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {row.original.cvr || '—'}
          </span>
        ),
      },
      {
        id: 'engagementCount',
        label: 'Engagements',
        accessorKey: 'engagementCount',
        sortable: true,
        size: 120,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontSize: 12, color: 'var(--fg2)' }}>{row.original.engagementCount}</span>
        ),
      },
      {
        id: 'tryggrLinked',
        label: 'Tryggr linked',
        accessorKey: 'tryggrLinked',
        sortable: true,
        filter: { kind: 'boolean' },
        size: 120,
        defaultVisible: false,
        cell: ({ row }) => (
          <span
            style={{ fontSize: 12, color: row.original.tryggrLinked ? '#4ade80' : 'var(--fg3)' }}
          >
            {row.original.tryggrLinked ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        id: 'lastActivity',
        label: 'Last activity',
        accessorKey: 'lastActivity',
        sortable: true,
        size: 140,
        defaultVisible: true,
        cell: ({ row }) => (
          <span style={{ fontSize: 12, color: 'var(--fg3)' }}>
            {formatDate(row.original.lastActivity)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 28,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)' }}>
            Clients
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg3)', marginTop: 2 }}>
            {rows.length > 0 ? `${rows.length} client${rows.length !== 1 ? 's' : ''}` : ''}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          + New client
        </button>
      </div>

      {loading && <div style={{ color: 'var(--fg3)', fontSize: 13 }}>Loading…</div>}
      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

      {!loading && !error && (
        <Workbench
          rows={rows}
          columns={columns}
          storageKey="grc-clients"
          session={session}
          onRowClick={(row) => navigate(`/clients/${encodeURIComponent(row.original.name)}`)}
          enableSelection={false}
          emptyMessage={
            rows.length === 0
              ? 'No clients yet. Create your first one above.'
              : 'No clients match your filters.'
          }
          searchPlaceholder="Search clients…"
        />
      )}

      {showModal && (
        <GrcNewClientModal
          onClose={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
    </div>
  );
}
